import { RefreshOutlined, SmsOutlined } from '@mui/icons-material';
// eslint-disable-next-line import/no-extraneous-dependencies
import DOMPurify from 'dompurify';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { demeterMarketIndicatorsApi } from '../../../Apis/Apis';
import {
    DeleteMarketIndicatorFactorRequest,
    DemeterFilterTimeSpan,
    DemeterPermissionType,
    DemeterUserType,
    MarketIndicatorCommentaryModel,
    UpdateMarketIndicatorFactorGroupRequest,
} from '../../../Generated/Raven-Demeter';
import NavigationRoutes from '../../../Layouts/NavigationRoutes';
import { useApplicationSelector } from '../../../Redux/ReduxStore';
import { selectUserType } from '../../../Redux/Slices/UserSlice';
import useApiWithoutAutoExecute from '../../Apis/Hooks/useApiWithoutAutoExecute';
import useCacheThenApi from '../../Apis/Hooks/useCacheThenApiHook';
import useMultipleApis from '../../Apis/Hooks/useMultipleApisHook';
import addNewFactorGroup from '../../Assets/Icons/expandGray.svg';
import BrandCard from '../../Components/BrandCard/BrandCard';
import ChartSkeleton from '../../Components/Charts/ChartWrapper/ChartSkeleton';
import MarketIndicatorChart from '../../Components/Charts/MarketIndicator/MarketIndicatorChart';
import FlipCard from '../../Components/Containers/FlipCard/FlipCard';
import PageHeaderWithBackNavigation from '../../Components/Headers/PageHeaderWithBackNavigation';
import PageHeaderWithTooltip from '../../Components/Headers/PageHeaderWithTooltip';
import CardSkeleton from '../../Components/Layout/CardSkeleton';
import MenuByMarketIndicatorNavigation from '../../Components/MenuFileSelectors/MenuByMarketIndicatorNavigation';
import useSearchParameters from '../../Components/Navigation/Hooks/useSearchParametersHook';
import browserService from '../../Services/BrowserService';
import CacheKeys from '../../Services/Cache/CacheKeys';
import formattingService from '../../Services/Formatting/FormattingService';
import useLanguage from '../../Services/Language/useLanguageHook';
import usePermission from '../../Services/Permissions/usePermissionHook';
import MarketIndicatorFactorGroup from './MarketIndicatorFactorGroup';
import styles from './MarketIndicatorFactorsPage.module.scss';
import MarketIndicatorGuageChart from './MarketIndicatorGuageChart';
import MarketIndicatorReviewForm from './MarketIndicatorReviewForm';

interface IMarketIndicatorsProps {
    isInAdministrationMode?: boolean;
}

const defaultNumberOfFactorsInRow = 4;
const maximumNumberOfFactorGroups = 4;
const skeletonCardCount = 16;

const MarketIndicatorsPage: React.FC<IMarketIndicatorsProps> = (props: IMarketIndicatorsProps) => {
    // Application hooks.
    const [translations, translate] = useLanguage();
    const navigate = useNavigate();
    const { marketIndicatorGuid, marketIndicatorFactorGuid } = useParams();
    const [searchParameters, setSearchParameters] = useSearchParameters();
    const currentUserType = useApplicationSelector(selectUserType);
    const [commentary, setCommentary] = useState<MarketIndicatorCommentaryModel | undefined>();
    const isAdministrator = useMemo(() => currentUserType === DemeterUserType.Administrator, [currentUserType]);

    const showMarketIndicatorChartAndFactorsDisclaimer =
        isAdministrator && browserService.getSessionStorageItem('playwright.showMarketIndicatorChartAndFactorsDisclaimer') === 'true';

    const showMarketIndicatorFactorsDisclaimer =
        isAdministrator && browserService.getSessionStorageItem('playwright.showMarketIndicatorFactorsDisclaimer') === 'true';

    const showMarketIndicatorFactorsHeader = isAdministrator && browserService.getSessionStorageItem('playwright.showMarketIndicatorFactorsHeader') === 'true';

    const showMarketIndicatorCommentary =
        isAdministrator && commentary && browserService.getSessionStorageItem('playwright.showMarketIndicatorCommentary') === 'true';

    // Api hooks.
    const [, , listMarketIndicatorsResponse] = useCacheThenApi(CacheKeys.ListMarketIndicatorsCurrent, () =>
        demeterMarketIndicatorsApi.listMarketIndicatorsCurrent(),
    );

    const [loadingMarketIndicator, refreshMarketIndicator, getMarketIndicatorResponse] = useApiWithoutAutoExecute(() =>
        demeterMarketIndicatorsApi.getMarketIndicator(marketIndicatorGuid!, true, props.isInAdministrationMode),
    );

    const [loadingRunMarketIndicator, refreshRunMarketIndicator, runMarketIndicatorResponse] = useApiWithoutAutoExecute(
        () => demeterMarketIndicatorsApi.runMarketIndicator(marketIndicatorGuid!),
        { errorMessage: translations.marketIndicators.marketIndicatorErrorMessage },
    );

    const [, getMarketIndicatorCommentary, getMarketIndicatorCommentaryResponse] = useApiWithoutAutoExecute(() => {
        if (searchParameters.marketIndicatorGuid) {
            return demeterMarketIndicatorsApi.getMarketIndicatorCommentary(searchParameters.marketIndicatorGuid);
        }
        if (marketIndicatorGuid) {
            return demeterMarketIndicatorsApi.getMarketIndicatorCommentary(marketIndicatorGuid);
        }
        return null;
    });

    const [loadingMarketIndicatorByRegionCommodity, refreshMarketIndicatorByRegionCommodity, getMarketIndicatorByRegionCommodityResponse] =
        useApiWithoutAutoExecute(() => {
            const { region, subRegion, commodity, dataSource } = listMarketIndicatorsResponse!.rows!.find(
                (x) => x.marketIndicatorGuid === searchParameters.marketIndicatorGuid,
            )!;
            return demeterMarketIndicatorsApi.getMarketIndicatorByRegionAndCommodity(region, commodity, subRegion ?? undefined, dataSource ?? undefined);
        });

    const showChartLoadingSpinner = loadingRunMarketIndicator || loadingMarketIndicator || loadingMarketIndicatorByRegionCommodity;

    const [, handleUpdateMarketIndicatorFactorsGroupName] = useApiWithoutAutoExecute(
        (marketIndicatorFactorRequest?: UpdateMarketIndicatorFactorGroupRequest) =>
            demeterMarketIndicatorsApi.updateMarketIndicatorFactorGroup(
                marketIndicatorFactorRequest!.marketIndicatorGuid,
                marketIndicatorFactorRequest!.displayGroupName,
                {
                    marketIndicatorGuid: marketIndicatorFactorRequest!.marketIndicatorGuid,
                    displayGroupName: marketIndicatorFactorRequest!.displayGroupName,
                    newDisplayGroupName: marketIndicatorFactorRequest!.newDisplayGroupName,
                },
            ),
        {
            successMessage: translations.marketIndicatorsManagement.messages.factorGroupNameUpdateSuccessful,
            errorMessage: translations.marketIndicatorsManagement.messages.factorGroupNameUpdateFailed,
        },
    );

    const [, handleDeleteMarketIndicatorFactors, deleteMarketIndicatorsFactorsResponse] = useMultipleApis(
        (marketIndicatorFactorRequest?: DeleteMarketIndicatorFactorRequest[]) => {
            const marketIndicatorFactorRequests = Array.isArray(marketIndicatorFactorRequest) ? marketIndicatorFactorRequest : [marketIndicatorFactorRequest];

            return marketIndicatorFactorRequests?.map((x) =>
                demeterMarketIndicatorsApi.deleteMarketIndicatorFactor(x!.marketIndicatorGuid, x!.marketIndicatorFactorGuid),
            );
        },
        {
            stopAutoExecute: true,
            successMessage: translations.marketIndicatorsManagement.messages.marketIndicatorFactorDeleteSuccessful,
            errorMessage: translations.marketIndicatorsManagement.messages.marketIndicatorFactorDeleteFailed,
        },
    );

    const marketFactorsResponse = useMemo(
        () => getMarketIndicatorResponse?.marketIndicator ?? getMarketIndicatorByRegionCommodityResponse?.marketIndicator,
        [getMarketIndicatorResponse, getMarketIndicatorByRegionCommodityResponse],
    );

    // Component states.
    const [addingFactorGroupOn, setAddingFactorGroupOn] = useState<boolean>();
    const guageChartTitleString = useMemo(() => translate(marketFactorsResponse?.displayName ?? ''), [translations, marketFactorsResponse]);

    useEffect(() => {
        if (!searchParameters.marketIndicatorGuid && !marketIndicatorGuid) {
            return;
        }
        getMarketIndicatorCommentary();
    }, [marketIndicatorGuid, searchParameters.marketIndicatorGuid]);

    useEffect(() => setCommentary(getMarketIndicatorCommentaryResponse?.marketIndicatorCommentary), [getMarketIndicatorCommentaryResponse]);

    // Wait until we have factor groups to avoid getting oops message.
    useEffect(() => {
        if (!props.isInAdministrationMode || !getMarketIndicatorResponse || getMarketIndicatorResponse?.marketIndicator?.factorGroups.length === 0) {
            return;
        }

        refreshRunMarketIndicator();
    }, [marketFactorsResponse, props.isInAdministrationMode]);

    useEffect(() => {
        if (!listMarketIndicatorsResponse || listMarketIndicatorsResponse.rows?.length === 0 || !searchParameters.marketIndicatorGuid) {
            return;
        }

        if (props.isInAdministrationMode) {
            refreshMarketIndicator();
        } else {
            refreshMarketIndicatorByRegionCommodity();
        }
    }, [
        searchParameters.marketIndicatorGuid,
        deleteMarketIndicatorsFactorsResponse,
        listMarketIndicatorsResponse,
        deleteMarketIndicatorsFactorsResponse,
        props.isInAdministrationMode,
    ]);

    useEffect(() => {
        if (!listMarketIndicatorsResponse || listMarketIndicatorsResponse?.rows?.length === 0 || searchParameters.marketIndicatorGuid) {
            return;
        }

        if (marketIndicatorGuid && marketIndicatorFactorGuid) {
            setSearchParameters({ marketIndicatorGuid, marketIndicatorFactorGuid });
            return;
        }

        if (searchParameters.marketIndicatorGuid && searchParameters.marketIndicatorFactorGuid) {
            return;
        }

        if (props.isInAdministrationMode && marketIndicatorGuid) {
            setSearchParameters({ marketIndicatorGuid });
        } else {
            setSearchParameters({ marketIndicatorGuid: listMarketIndicatorsResponse?.rows![0].marketIndicatorGuid! });
        }
    }, [
        props.isInAdministrationMode,
        listMarketIndicatorsResponse,
        marketIndicatorGuid,
        marketIndicatorFactorGuid,
        searchParameters.marketIndicatorGuid,
        searchParameters.marketIndicatorFactorGuid,
    ]);

    const navigateBackToMarketIndicators = () => {
        setSearchParameters({ tab: 'MarketIndicators' });
        // Change this back to navigate(-1) after getting out of mono.
        navigate(NavigationRoutes.Administration);
    };

    const loading = !marketFactorsResponse || !listMarketIndicatorsResponse;

    const selectedMarketIndicator = runMarketIndicatorResponse?.marketIndicator ?? marketFactorsResponse!;
    const marketIndicatorPermissionKey = selectedMarketIndicator ? `${selectedMarketIndicator.region}_${selectedMarketIndicator.commodity}` : '';
    const isReadOnlyMode =
        usePermission(DemeterPermissionType.MarketIndicatorSubjectMatterExpert, marketIndicatorPermissionKey) &&
        currentUserType === DemeterUserType.BusinessOwner;
    const showReviewForm =
        props.isInAdministrationMode && usePermission(DemeterPermissionType.MarketIndicatorSubjectMatterExpert, marketIndicatorPermissionKey);
    const isInEditMode = props.isInAdministrationMode && usePermission(DemeterPermissionType.MarketIndicatorDeveloper);

    return (
        <div className={styles.master_page_container} data-testid="MarketIndicatorsPage">
            {props.isInAdministrationMode ? (
                <PageHeaderWithBackNavigation
                    handleBackNavigation={navigateBackToMarketIndicators}
                    title={translations.marketIndicatorsManagement.title}
                    testId="MarketIndicatorsPageHeader"
                    track={{ eventCategory: 'MarketIndicators', eventAction: 'BackClicked' }}
                />
            ) : (
                <>
                    <PageHeaderWithTooltip
                        title={translations.marketIndicators.title}
                        testId="MarketIndicatorsPageHeader"
                        tooltip={translations.marketIndicators.tooltipMessage}
                    />
                    {listMarketIndicatorsResponse && (
                        <MenuByMarketIndicatorNavigation
                            listMarketIndicatorsResponse={listMarketIndicatorsResponse}
                            track={{ eventCategory: 'MarketIndicators', eventAction: 'Menu' }}
                        />
                    )}
                </>
            )}
            {loading && (
                <div className={styles.market_indicators_skeleton_wrapper}>
                    <div className={styles.market_indicators_skeleton_layout}>
                        <div className={styles.market_indicators_skeleton_card}>
                            <CardSkeleton />
                        </div>
                        <div className={styles.market_indicators_skeleton_grid}>
                            {Array.from({ length: skeletonCardCount }, (_, index) => `skeleton-card-${index}`).map((key) => (
                                <CardSkeleton key={key} minimumHeight={120} />
                            ))}
                        </div>
                    </div>
                    <div className={styles.markets_management_timeline_chart}>
                        <ChartSkeleton />
                    </div>
                </div>
            )}
            {!loading && (
                <div className={styles.markets_management_indicator_container} data-testid="MarketsManagementIndicatorContainer">
                    <div className={styles.markets_management_indicator_row} data-testid="MarketsManagementIndicatorRow">
                        <div className={styles.markets_management_indicators_reverse} data-testid="MarketsManagementIndicators">
                            {showMarketIndicatorFactorsHeader && (
                                <div className={styles.markets_management_factors_header}>
                                    <h2>{guageChartTitleString}</h2>
                                </div>
                            )}
                            {(runMarketIndicatorResponse?.marketIndicator?.factorGroups ?? marketFactorsResponse.factorGroups)
                                .filter((x) => x.factors.length > 0)
                                .map((x) => (
                                    <MarketIndicatorFactorGroup
                                        key={x.displayGroupName}
                                        displayName={x.displayGroupName}
                                        factors={x.factors.sort((a, b) => a.order - b.order)}
                                        handleUpdateMarketIndicatorFactorsGroup={handleUpdateMarketIndicatorFactorsGroupName}
                                        handleDeleteMarketIndicatorFactors={handleDeleteMarketIndicatorFactors}
                                        isInEditMode={isInEditMode}
                                        isInAdministrationMode={props.isInAdministrationMode}
                                        isReadOnlyMode={isReadOnlyMode}
                                        nextOrder={x.factors.sort((a, b) => a.order - b.order)[x.factors.length - 1].order + 1}
                                        getMarketIndicatorResponse={getMarketIndicatorByRegionCommodityResponse}
                                    />
                                ))}
                            {addingFactorGroupOn && (
                                <MarketIndicatorFactorGroup
                                    displayName={translations.marketIndicatorsManagement.text.newFactorGroup}
                                    factors={[]}
                                    handleUpdateMarketIndicatorFactorsGroup={handleUpdateMarketIndicatorFactorsGroupName}
                                    handleDeleteMarketIndicatorFactors={() => setAddingFactorGroupOn(false)}
                                    isInEditMode={isInEditMode}
                                    isInAdministrationMode={props.isInAdministrationMode}
                                    nextOrder={
                                        (marketFactorsResponse?.factorGroups[(marketFactorsResponse?.factorGroups.length ?? 0) - 1]?.order ?? 0) +
                                        defaultNumberOfFactorsInRow
                                    }
                                />
                            )}
                            {isInEditMode && marketFactorsResponse?.factorGroups.filter((x) => x.factors.length > 0).length < maximumNumberOfFactorGroups && (
                                <button type="button" onClick={() => setAddingFactorGroupOn(true)} className={styles.markets_management_add_factor_group}>
                                    <img src={addNewFactorGroup} alt="#" /> {translations.marketIndicatorsManagement.text.addFactorGroup}
                                </button>
                            )}
                            {showMarketIndicatorFactorsDisclaimer && (
                                <div className={styles.market_indicator_disclaimer}>{translations.marketIndicators.tooltipMessage}</div>
                            )}
                        </div>
                        <div className={styles.markets_management_guage_reverse}>
                            <FlipCard
                                hoverBlurAmount={3}
                                width={500}
                                height={570}
                                enableHoverBlurFirst
                                iconToBack={<SmsOutlined fontSize="medium" />}
                                iconToFront={<RefreshOutlined fontSize="medium" />}
                                tooltipToBack={translations.marketIndicatorsManagement.text.viewCommentary}
                                tooltipToFront={translations.marketIndicatorsManagement.text.viewGuageChart}
                            >
                                <MarketIndicatorGuageChart
                                    title={guageChartTitleString}
                                    marketIndicator={runMarketIndicatorResponse?.marketIndicator ?? marketFactorsResponse!}
                                />
                                {commentary && (
                                    <BrandCard
                                        brandingTitleString={commentary.title ?? ''}
                                        textAlign="left"
                                        track={{ eventCategory: 'MarketIndicators', eventAction: 'Commentary' }}
                                    >
                                        <div className={styles.markets_management_commentary}>
                                            <div className={styles.markets_management_commentary_author}>{commentary.author}</div>
                                            {commentary.publishedDate && (
                                                <div className={styles.markets_management_commentary_date}>
                                                    {formattingService.toLongFormatDate(new Date(commentary.publishedDate))}
                                                </div>
                                            )}
                                            <div
                                                className={styles.markets_management_commentary_content}
                                                // eslint-disable-next-line react/no-danger
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(commentary.content ?? ''),
                                                }}
                                            />
                                        </div>
                                    </BrandCard>
                                )}
                            </FlipCard>
                        </div>
                    </div>
                    {showMarketIndicatorChartAndFactorsDisclaimer && (
                        <div className={styles.market_indicator_disclaimer}>{translations.marketIndicators.tooltipMessage}</div>
                    )}
                </div>
            )}
            {!loading && (
                <div className={styles.markets_management_timeline_chart}>
                    <MarketIndicatorChart
                        isLoading={showChartLoadingSpinner}
                        useColorPalletteInLegend
                        runMarketIndicatorResponse={runMarketIndicatorResponse ?? getMarketIndicatorResponse ?? getMarketIndicatorByRegionCommodityResponse}
                        chartNavigatorTimeRange={DemeterFilterTimeSpan.ThreeMonths}
                        testId="MarketIndicatorChart"
                        track={{ eventCategory: 'MarketIndicators', eventAction: 'MarketIndicatorChart' }}
                    />
                </div>
            )}
            {!loading && props.isInAdministrationMode && (
                <MarketIndicatorReviewForm marketIndicatorGuid={marketIndicatorGuid ?? searchParameters.marketIndicatorGuid} show={showReviewForm} />
            )}
            {!loading && showMarketIndicatorCommentary && (
                <div className={styles.markets_management_commentary_container} data-testid="MarketIndicatorCommentaryContainer">
                    <BrandCard
                        brandingTitleString={commentary.title ?? ''}
                        textAlign="left"
                        track={{ eventCategory: 'MarketIndicators', eventAction: 'Commentary' }}
                    >
                        <div className={styles.markets_management_commentary}>
                            <div className={styles.markets_management_commentary_author}>{commentary.author}</div>
                            {commentary.publishedDate && (
                                <div className={styles.markets_management_commentary_date}>
                                    {formattingService.toLongFormatDate(new Date(commentary.publishedDate))}
                                </div>
                            )}
                            <div
                                className={styles.markets_management_commentary_content}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(commentary.content ?? ''),
                                }}
                            />
                        </div>
                    </BrandCard>
                </div>
            )}
        </div>
    );
};

export default MarketIndicatorsPage;
